import React, { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../lib/GlobalVar';
import { ConsultaArquivos, ConsultaFileServer, ListaArquivosSamba, BaixaArquivoSamba } from '../lib/api';
import Swal from 'sweetalert2';
import Icon from './Icon';
import ArquivosAcessosTI from './ArquivosAcessosTI';

// 22/10/2026: a conta Samba de cada pessoa e escolhida pela TI (painel "Contas e acessos"); sem conta associada = sem acesso.
// A pessoa nao digita nem ve senha: a tela entra no servidor com a conta dela.
const ehTI = (u) => String(u?.setor || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase() === 'ti';

const fmtTam = (b) => {
    if (!b) return '—';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let i = 0, n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
};
const fmtData = (d) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleString('pt-BR'); } catch { return '—'; }
};

export default function Arquivos() {
    const { user } = useContext(GlobalContext);
    const [host, setHost] = useState('');
    const [shares, setShares] = useState([]);
    const [conectado, setConectado] = useState(true);
    const [config, setConfig] = useState(null);
    const [semAcesso, setSemAcesso] = useState('');   // a mensagem de "sem acesso" (sem conta associada pela TI)
    const [conta, setConta] = useState(null);         // { usuario, dominio } da conta com que a pessoa entra
    const [painelTI, setPainelTI] = useState(false);

    // Navegador
    const [aberto, setAberto] = useState(null);
    const [pathAtual, setPathAtual] = useState('');
    const [itens, setItens] = useState([]);
    const [navLoading, setNavLoading] = useState(false);
    const [navErro, setNavErro] = useState('');
    const [preview, setPreview] = useState(null);

    useEffect(() => { reload(); }, []);

    async function reload() {
        const [arq, cfg] = await Promise.all([ConsultaArquivos({ solicitante: user?.email }), ConsultaFileServer({})]);
        if (arq.Error) { setShares([]); setConectado(false); setSemAcesso(''); setConta(null); }
        else if (arq.semAcesso) { setShares([]); setHost(arq.host); setConectado(false); setSemAcesso(arq.mensagem || 'Sem acesso a arquivos.'); setConta(null); }
        else { setShares(arq.shares || []); setHost(arq.host); setConectado(true); setSemAcesso(''); setConta({ usuario: arq.usuario, dominio: arq.dominio }); }
        if (!cfg.Error) setConfig(cfg);
    }

    async function abrirShare(s) {
        setAberto(s);
        setPathAtual('');
        await listar(s, '');
    }

    async function listar(share, path) {
        setNavLoading(true);
        setNavErro('');
        const resp = await ListaArquivosSamba({
            solicitante: user?.email,
            server_id: share?.server_id || share?.id,
            share: share.unc || share.path || share.nome,
            path,
        });
        setNavLoading(false);
        if (resp.Error) {
            setItens([]);
            setNavErro(resp.Error);
            return;
        }
        setItens(resp.itens || []);
    }

    function entrar(it) {
        if (it.tipo !== 'dir') return;
        const novo = pathAtual ? `${pathAtual}\\${it.nome}` : it.nome;
        setPathAtual(novo);
        listar(aberto, novo);
    }

    function voltar() {
        if (!pathAtual) { setAberto(null); setItens([]); return; }
        const partes = pathAtual.split('\\');
        partes.pop();
        const novo = partes.join('\\');
        setPathAtual(novo);
        listar(aberto, novo);
    }

    function irPara(idx) {
        const partes = pathAtual.split('\\').filter(Boolean);
        const novo = partes.slice(0, idx + 1).join('\\');
        setPathAtual(novo);
        listar(aberto, novo);
    }

    function b64ToBlob(b64, mime) {
        const bin = atob(b64); const arr = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
        return new Blob([arr], { type: mime || 'application/octet-stream' });
    }
    function baixarBlob(b64, mime, nome) {
        const url = URL.createObjectURL(b64ToBlob(b64, mime));
        const a = document.createElement('a'); a.href = url; a.download = nome; document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 4000);
    }
    async function baixarArquivo(it) {
        const p = (pathAtual ? pathAtual + '\\' : '') + it.nome;
        const r = await BaixaArquivoSamba({ solicitante: user?.email, server_id: aberto?.server_id || aberto?.id, path: p });
        if (r.Error) return Swal.fire({ icon: 'error', title: 'Falhou', text: r.Error });
        baixarBlob(r.base64, r.mime, r.nome);
    }
    async function verArquivo(it) {
        setPreview({ carregando: true, nome: it.nome });
        const p = (pathAtual ? pathAtual + '\\' : '') + it.nome;
        const r = await BaixaArquivoSamba({ solicitante: user?.email, server_id: aberto?.server_id || aberto?.id, path: p });
        if (r.Error) { setPreview(null); return Swal.fire({ icon: 'error', title: 'Falhou', text: r.Error }); }
        setPreview({ carregando: false, nome: r.nome, mime: r.mime, base64: r.base64, dataUrl: `data:${r.mime};base64,${r.base64}` });
    }

    const breadcrumb = pathAtual ? pathAtual.split('\\').filter(Boolean) : [];
    const usuarioConta = conta ? ((conta.dominio ? conta.dominio + '\\' : '') + conta.usuario) : '—';

    return (
        <div className="page">
            <div className="page-head">
                <div>
                    <div className="eyebrow"><span className="num">04</span> Acesso a Arquivos</div>
                    <h1 className="page-title">Acesso a Arquivos</h1>
                    <p className="page-sub">
                        Compartilhamentos SMB do <span className="mono">\\{host || 'fileserver-babita'}</span> com o usuário Samba que a TI associou a você: você vê as pastas que esse usuário vê.
                    </p>
                </div>
                {ehTI(user) && (
                    <button className="btn" onClick={() => setPainelTI((v) => !v)}>
                        <Icon name="settings" size={11}/> {painelTI ? 'Esconder contas e acessos' : 'Contas e acessos (TI)'}
                    </button>
                )}
            </div>

            {painelTI && <ArquivosAcessosTI onFechar={() => { setPainelTI(false); reload(); }} />}

            <div className="banner">
                <span className={`dot ${conectado ? 'ok' : 'err'}`}/>
                <div style={{ flex: 1 }}>
                    <div className="banner-title">{conectado ? 'Conectado ao file server' : (semAcesso ? 'Sem acesso a arquivos' : 'Sem conexão')}</div>
                    <div className="banner-sub mono">
                        \\{host || 'fileserver-babita'} · {config?.smb_versao || 'SMB3'} · usuário {usuarioConta}
                    </div>
                </div>
                <span className={`status-tag ${conectado ? 'ok' : 'err'}`}>{conectado ? 'SMB ATIVO' : (semAcesso ? 'SEM ACESSO' : 'SMB OFF')}</span>
            </div>

            {semAcesso && (
                <div className="empty" style={{ marginBottom: 18 }}>{semAcesso}</div>
            )}

            {!aberto && (
                <div className="shares-grid">
                    {shares.map(s => (
                        <div className="share-card" key={s.id || s.path || s.nome}>
                            <div className="share-eyebrow">{s.setor || 'Babita'}</div>
                            <div className="share-name">{s.nome}</div>
                            <div className="share-path mono">{s.unc || s.path}</div>
                            <div style={{ display: 'flex', gap: 14, marginTop: 12, flexWrap: 'wrap' }}>
                                <span className="share-actions" onClick={() => abrirShare(s)}>
                                    <Icon name="folder" size={12}/> NAVEGAR
                                </span>
                            </div>
                        </div>
                    ))}
                    {shares.length === 0 && !semAcesso && <div className="empty" style={{ gridColumn: '1 / -1' }}>Sem shares cadastrados.</div>}
                </div>
            )}

            {aberto && (
                <div className="card" style={{ padding: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderBottom: '1px solid var(--line)' }}>
                        <button className="btn ghost sm" onClick={voltar}><Icon name="chevron-left" size={11}/> Voltar</button>
                        <div className="mono" style={{ fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <span style={{ cursor: 'pointer' }} onClick={() => { setPathAtual(''); listar(aberto, ''); }}>
                                {aberto.nome}
                            </span>
                            {breadcrumb.map((p, i) => (
                                <span key={i}>
                                    <span style={{ color: 'var(--ink-mute)', margin: '0 4px' }}>\</span>
                                    <span style={{ cursor: 'pointer' }} onClick={() => irPara(i)}>{p}</span>
                                </span>
                            ))}
                        </div>
                        <button className="btn ghost sm" onClick={() => { setAberto(null); setItens([]); setPathAtual(''); }}>Fechar</button>
                    </div>

                    {navErro && (
                        <div className="empty" style={{ border: 'none', color: 'var(--status-err)', padding: '20px 22px' }}>
                            {navErro}
                        </div>
                    )}

                    {!navErro && navLoading && (
                        <div className="empty" style={{ border: 'none' }}>Carregando…</div>
                    )}

                    {!navErro && !navLoading && (
                        <table className="preview-table" style={{ margin: 0 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 32 }}></th>
                                    <th>Nome</th>
                                    <th style={{ width: 120 }}>Tamanho</th>
                                    <th style={{ width: 180 }}>Modificado</th>
                                    <th style={{ width: 130 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {itens.map((it, i) => (
                                    <tr key={i} style={{ cursor: it.tipo === 'dir' ? 'pointer' : 'default' }} onClick={() => entrar(it)}>
                                        <td><Icon name={it.tipo === 'dir' ? 'folder' : 'file'} size={14}/></td>
                                        <td className="mono">{it.nome}</td>
                                        <td className="mono" style={{ color: 'var(--ink-mute)' }}>{it.tipo === 'dir' ? '—' : fmtTam(it.tamanho)}</td>
                                        <td className="mono" style={{ color: 'var(--ink-mute)' }}>{fmtData(it.modificado)}</td>
                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {it.tipo !== 'dir' && (
                                                <span onClick={e => e.stopPropagation()} style={{ display: 'inline-flex', gap: 6 }}>
                                                    <button className="btn ghost sm" onClick={() => verArquivo(it)}>Ver</button>
                                                    <button className="btn ghost sm" onClick={() => baixarArquivo(it)}>Baixar</button>
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                {itens.length === 0 && (
                                    <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--ink-mute)', padding: '24px' }}>Pasta vazia</td></tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
            {preview && (
                <div className="modal-mask" onClick={() => setPreview(null)}>
                    <div className="modal" style={{ maxWidth: 920, width: '92%' }} onClick={e => e.stopPropagation()}>
                        <div className="modal-head">
                            <div className="eyebrow mono">{preview.nome}</div>
                            <button className="icon-btn" onClick={() => setPreview(null)}><Icon name="x" size={14}/></button>
                        </div>
                        <div className="modal-body" style={{ maxHeight: '72vh', overflow: 'auto' }}>
                            {preview.carregando ? <div className="muted">Carregando…</div> :
                                (preview.mime || '').startsWith('image/') ? <img src={preview.dataUrl} alt={preview.nome} style={{ maxWidth: '100%' }}/> :
                                preview.mime === 'application/pdf' ? <iframe title="pdf" src={preview.dataUrl} style={{ width: '100%', height: '68vh', border: 0 }}/> :
                                ((preview.mime || '').startsWith('text/') || preview.mime === 'application/json' || preview.mime === 'application/xml') ?
                                    <pre className="mono" style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{(() => { try { return decodeURIComponent(escape(atob(preview.base64))); } catch (_) { try { return atob(preview.base64); } catch (e) { return '(binário)'; } } })()}</pre> :
                                    <div className="muted">Pré-visualização não disponível para este tipo. Use o botão Baixar.</div>}
                        </div>
                        <div className="modal-foot">
                            <button className="btn sm" onClick={() => baixarBlob(preview.base64, preview.mime, preview.nome)}>Baixar</button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
